import { PatchJobServiceData } from 'services/GameHosting/PatchJob/patchingServiceType';
import { Invoice } from 'types/account/invoice/invoice';
import { Application } from 'types/GameHosting/application';
import { ApplicationBuild } from 'types/GameHosting/application-build';
import { DeploymentEnvironment, DeploymentProfile, DeploymentRegion } from 'types/GameHosting/deployment';
import { Fleet } from 'types/GameHosting/fleet';
import { ApplicationInstance } from 'types/GameHosting/instance';
import { GameTemplate } from 'types/GameHosting/template';
import { Ticket } from 'types/tickets/tickets';

const PAGES = {
  home: '/',
  identity: {
    signup: '/signup',
    validateEmail: '/validate-email',
  },
  application: {
    overview: '/Game-Hosting/Application-Management/Applications',
    detail: (id: Application['id'] | undefined) => `/Game-Hosting/Application-Management/Application/${id}`,
  },
  build: {
    overview: '/Game-Hosting/Application-Management/Builds',
    detail: (id: ApplicationBuild['id'] | undefined) => `/Game-Hosting/Application-Management/ApplicationBuild/${id}`,
  },
  compute: {
    servers: {
      overview: '/Servers',
      detail: (serverId: string | number) => `/Servers/${serverId}`,
      usage: (serverId: string | number) => `/Servers/${serverId}/Usage`,
      alerts: (serverId: string | number) => `/Servers/${serverId}/Alerts`,
      trialServer: '/Server/Trial-Server',
    },
  },
  cdn: {
    product: '/CDN',
  },
  antiDdos: {
    product: '/Anti-DDos',
  },
  gameHosting: {
    antiCheat: { product: '/Anti-Cheat' },
    overview: '/Game-Hosting',
    dashboard: '/Game-Hosting/Dashboard',
    applicationManagement: '/Game-Hosting/Application-Management',
    deploymentManagement: '/Game-Hosting/Deployment-Management',
  },
  instance: {
    overview: '/Game-Hosting/Instance-Management',
    crashes: '/Game-Hosting/Instance-Management/Crashes',
    detail: (id: ApplicationInstance['id'] | undefined) => `/Game-Hosting/Instance/${id}`,
  },
  deploymentEnvironment: {
    overview: '/Game-Hosting/Deployment-Management/Environments',
    detail: (id: DeploymentEnvironment['id'] | undefined) => `/Game-Hosting/Deployment-Management/Environments/${id}`,
  },
  patching: {
    overview: '/Game-Hosting/Patching',
    job: { detail: (patchJobId: PatchJobServiceData['id']) => `/Game-Hosting/Patching/Job/${patchJobId}` },
  },
  template: {
    overview: '/Game-Hosting/Template-Management',
    game: { detail: (gameTemplateId: GameTemplate['id']) => `/Game-Hosting/Template-Management/Game/${gameTemplateId}` },
    utility: { detail: (utilityTemplateId: GameTemplate['id']) => `/Game-Hosting/Template-Management/Utility/${utilityTemplateId}` },
    dependency: { detail: (dependencyTemplateId: GameTemplate['id']) => `/Game-Hosting/Template-Management/Dependency/${dependencyTemplateId}` },
    hostCapacity: '/Game-Hosting/Template-Management/Host-Capacity',
  },
  cloud: {
    overview: '/Game-Hosting/Cloud-Accounts',
  },
  gameLiftAccount: {
    overview: '/Game-Hosting/GameLift-Accounts',
  },
  deploymentProfile: {
    overview: '/Game-Hosting/Deployment-Management/Profiles',
    detail: (id: DeploymentProfile['id'] | undefined) => `/Game-Hosting/Deployment-Management/Profiles/Overview/${id}`,
    region: (profileId: DeploymentProfile['id'] | undefined, regionId: DeploymentRegion['id'] | undefined) =>
      `/Game-Hosting/Deployment-Management/Profiles/Overview/${profileId}/Region/${regionId}`,
  },
  fleet: {
    overview: '/Game-Hosting/Deployment-Management/Fleets',
    detail: (id: Fleet['id'] | undefined) => `/Game-Hosting/Deployment-Management/Fleets/${id}`,
  },
  ticket: {
    overview: '/Support/Tickets',
    newOverview: 'https://customer.service.i3d.support/servicedesk/customer/user/requests',
    create: 'https://customer.service.i3d.support/servicedesk/customer/portals',
    detail: (id: Ticket['id'], hash: Ticket['hash']) => `/Support/Tickets/T-${id}-${hash}`,
    account: { changeBusinessDetails: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68' },
    bug: { create: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/8/create/15' },
    demo: { create: 'https://www.i3d.net/contact/get-demo/' },
    server: {
      reactivate: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68',
      hardwareUpgrade: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68',
      order: 'https://www.i3d.net/contact/get-quote/?product=Bare%20Metal%20Server',
    },
    colocation: {
      reactivate: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68',
      upgrade: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68',
      ipv6Upgrade: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68',
    },
    connectivity: {
      reactivate: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/68',
      order: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/create/49',
    },
    cdn: { order: 'https://www.i3d.net/contact/get-quote/?product=CDN' },
  },
  account: {
    overview: `/Account`,
    profile: { overview: `/Profile` },
    invoice: {
      overview: '/Invoices',
      detail: (id: Invoice['id']) => `/Invoice/${id}`,
    },
  },
  crossConnect: {
    overview: '/Cross-Connects',
    detail: (crossConnectId: string) => `/Cross-Connects/${crossConnectId}`,
  },
  colocation: {
    overview: '/Colocation',
    detail: (colocationId: string) => `/Colocation/${colocationId}`,
  },
  dataCenter: {
    overview: '/Data-Center-Access',
    detail: (accessId: string) => `/Data-Center-Access/Details/${accessId}`,
  },
  connectivity: {
    overview: '/Connectivity',
    detail: (connectivityId: string) => `/Connectivity/${connectivityId}`,
    details: (connectivityId: string) => `/Connectivity/${connectivityId}/Details`,
    usage: (connectivityId: string) => `/Connectivity/${connectivityId}/Usage`,
    invoices: (connectivityId: string) => `/Connectivity/${connectivityId}/Contract-&-Invoices`,
  },
  support: {
    remoteHand: 'https://customer.service.i3d.support/servicedesk/customer/portal/3/group/9/create/35',
    documentation: '/Documentation',
  },
};

export default PAGES;
